import { type FC, useMemo } from "react";
import { AppImage } from "@components/NextImage";
import { motion } from "motion/react";
import { cn } from "utils";
import {
  INTRO3_BAR_FILL_DURATION_S,
  INTRO3_SHIMMER_FADE_DURATION_S,
  INTRO3_SHIMMER_REPEAT_DELAY_S,
  INTRO3_SHIMMER_SWEEP_DURATION_S,
  intro3BarFillDelayS,
  intro3CardStaggerContainer,
  intro3GoalCardEnter,
  intro3ShimmerDelayS,
} from "../introMotionPresets";
import { getIntro3GoalCards } from "../introSlidesConfig";
import { createSignInIntroCopy } from "../signInIntroCopy";
import type { IntroHeroProps } from "../types";
import { useIntroIsActive } from "../IntroActiveIndexContext";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";
import { useParams } from "next/navigation";

type IntroSlide3Layout = "mobile" | "desktop";

interface IntroSlide3GoalsProps extends Omit<IntroHeroProps, "isActive"> {
  /** H5 全宽 / PC 右栏视觉层 */
  layout?: IntroSlide3Layout;
}

export const IntroSlide3Goals: FC<IntroSlide3GoalsProps> = ({
  layout = "mobile",
}) => {
  const isActive = useIntroIsActive(2);
  const reducedMotion = usePrefersReducedMotion();
  const { locale = "en" } = useParams() as { locale?: string };
  const goalCards = useMemo(
    () => getIntro3GoalCards(createSignInIntroCopy(locale)),
    [locale],
  );
  const isDesktop = layout === "desktop";

  return (
    <motion.div
      className={cn(
        "relative mx-auto w-full",
        isDesktop ? "max-w-[409px]" : "max-w-[316px]",
      )}
      data-testid="intro-slide-3-goals"
      data-node-id={isDesktop ? "96344:109912" : "96293:45206"}
      data-layout={layout}
      variants={intro3CardStaggerContainer}
      initial="hidden"
      animate={isActive ? "visible" : "hidden"}
    >
      <div className={cn("flex flex-col", isDesktop ? "gap-4" : "gap-3.5")}>
        {goalCards.map((card, cardIndex) => {
          const barDelay = intro3BarFillDelayS(cardIndex);
          const shimmerDelay = intro3ShimmerDelayS(cardIndex);

          return (
            <motion.div
              key={card.id}
              variants={intro3GoalCardEnter}
              data-testid={`intro-slide-3-goal-${card.id}`}
              className={cn(
                "w-full max-w-[292px] border border-border_inverse_transparent10",
                "rounded-xl bg-[rgba(1,38,43,0.9)]",
                isDesktop
                  ? "max-w-[409px] rounded-2xl border-[1.4px] p-5 gap-[13px]"
                  : "p-3.5 gap-[9px]",
                "flex flex-col",
                card.mobileOffsetClassName,
                card.desktopOffsetClassName,
              )}
            >
              <div
                className={cn(
                  "flex items-center overflow-hidden",
                  isDesktop ? "gap-2.5" : "gap-2",
                )}
              >
                <AppImage
                  src={card.icon}
                  alt=""
                  width={isDesktop ? 34 : 24}
                  height={isDesktop ? 34 : 24}
                  className={cn(
                    "shrink-0",
                    isDesktop ? "size-[34px]" : "size-6",
                  )}
                />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-content_inverse_primary",
                    isDesktop
                      ? "text-[22px] font-semibold leading-[34px]"
                      : "title_l",
                  )}
                >
                  {card.title}
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded bg-content_aura_lv3_primary text-content_primary",
                    isDesktop
                      ? "px-3.5 py-1.5 text-[17px] font-semibold leading-[22px]"
                      : "px-2.5 py-1 body_bold_s",
                  )}
                >
                  {card.percent}%
                </span>
              </div>

              <div
                className={cn(
                  "relative overflow-hidden rounded-[3px] bg-[rgba(255,255,255,0.15)]",
                  isDesktop ? "h-2 rounded" : "h-1.5",
                )}
              >
                <motion.div
                  className={cn(
                    "absolute inset-y-0 left-0 overflow-hidden rounded-[3px]",
                    "bg-[rgba(255,255,255,0.4)] shadow-[0px_0px_6px_1px_rgba(242,255,135,0.2)]",
                    isDesktop &&
                      "rounded shadow-[0px_0px_8.4px_1.4px_rgba(242,255,135,0.2)]",
                  )}
                  initial={
                    reducedMotion
                      ? { width: `${card.percent}%` }
                      : { width: "0%" }
                  }
                  animate={
                    isActive
                      ? { width: `${card.percent}%` }
                      : { width: reducedMotion ? `${card.percent}%` : "0%" }
                  }
                  transition={
                    reducedMotion
                      ? { duration: 0 }
                      : {
                          duration: INTRO3_BAR_FILL_DURATION_S,
                          delay: barDelay,
                          ease: "easeOut",
                        }
                  }
                >
                  {isActive && !reducedMotion ? (
                    <motion.div
                      className={cn(
                        "pointer-events-none absolute inset-y-0 w-6",
                        "bg-gradient-to-r from-transparent via-white/75 to-transparent blur-[1px]",
                        isDesktop && "w-9 blur-[1.4px] via-white/65",
                      )}
                      initial={{ opacity: 0, x: "-120%" }}
                      animate={{
                        opacity: 1,
                        x: ["-120%", "90%"],
                      }}
                      transition={{
                        opacity: {
                          duration: INTRO3_SHIMMER_FADE_DURATION_S,
                          delay: shimmerDelay,
                          ease: "easeInOut",
                        },
                        x: {
                          duration: INTRO3_SHIMMER_SWEEP_DURATION_S,
                          delay: shimmerDelay,
                          ease: "easeInOut",
                          repeat: Infinity,
                          repeatDelay: INTRO3_SHIMMER_REPEAT_DELAY_S,
                        },
                      }}
                    />
                  ) : null}
                </motion.div>
              </div>

              <p
                className={cn(
                  "text-[rgba(255,255,255,0.55)]",
                  isDesktop ? "body_medium_l" : "body_medium_s",
                )}
              >
                {card.amountLabel}
              </p>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};
